import {
  CpfFormField,
  FloatingFormItem,
  Group,
  InputText,
  PhoneFormField,
} from '@/components/_common';
import { useSpedSettingsEdit } from '@/core/domain/sped/sped-settings/sped-settings.hooks';
import { SpedSettings } from '@/core/domain/sped/sped-settings/sped-settings.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { applyMask, removeMask } from '@/utils/mask';
import { cpfMask, phoneMask } from '@/utils/mask/commonMasks';
import { Button, Card, Col, Divider, Form, Input, Row } from 'antd';
import { useEffect } from 'react';

interface Props {
  spedSettings: SpedSettings;
}

interface FormValues {
  name: string;
  phone: string;
  cpf: string;
  crc: string;
  email: string;
  address?: string;
  municipalCode: string;
}

export default function SpedSettingsEdit({ spedSettings }: Props) {
  const fieldRequired = useDefaultEmptyFieldError();

  const [form] = Form.useForm<FormValues>();

  const spedSettingsEdit = useSpedSettingsEdit();

  const isLoading = spedSettingsEdit.isLoading;

  const disabledField = !hasPermissions([Authorities.SPED_CONFIG_EDIT]);

  useEffect(() => {
    if (spedSettings) {
      form.setFieldsValue({
        ...spedSettings,
        phone: spedSettings.phone
          ? applyMask(spedSettings.phone, phoneMask)
          : undefined,
        cpf: spedSettings.cpf
          ? applyMask(spedSettings.cpf, cpfMask)
          : undefined,
      });
    }
  }, [spedSettings]);

  const handleSubmit = async (values: FormValues) => {
    if (!spedSettings) return;

    await spedSettingsEdit.mutateAsync({
      ...values,
      phone: removeMask(values.phone),
      cpf: removeMask(values.cpf),
      companyId: spedSettings.companyId,
      id: spedSettings.id,
    });
  };

  return (
    <Card style={{ borderRadius: 8 }} id="sped-settings-form">
      <Form form={form} onFinish={handleSubmit}>
        <strong>Dados do contador</strong>

        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col span={24} sm={12}>
            <FloatingFormItem
              name="name"
              label="Nome completo"
              rules={[fieldRequired]}
            >
              <Input disabled={isLoading || disabledField} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={6}>
            <PhoneFormField
              name="phone"
              label="Telefone"
              required
              disabled={isLoading || disabledField}
            />
          </Col>

          <Col span={24} sm={6}>
            <FloatingFormItem
              name="email"
              label="E-mail"
              rules={[
                fieldRequired,
                { type: 'email', message: 'E-mail inválido' },
              ]}
            >
              <Input disabled={isLoading || disabledField} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={6}>
            <CpfFormField
              name="cpf"
              label="CPF"
              required
              disabled={isLoading || disabledField}
            />
          </Col>

          <Col span={24} sm={6}>
            <FloatingFormItem name="crc" label="CRC" rules={[fieldRequired]}>
              <InputText onlyNumber disabled={isLoading || disabledField} />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="address"
              label="Endereço"
              rules={[fieldRequired]}
            >
              <Input.TextArea disabled={isLoading || disabledField} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={6}>
            <FloatingFormItem
              name="municipalCode"
              label="Cód. Municipal"
              rules={[fieldRequired]}
            >
              <Input disabled={isLoading || disabledField} />
            </FloatingFormItem>
          </Col>
        </Row>

        <Divider />

        <Group position="right">
          <Button
            htmlType="submit"
            type="primary"
            loading={isLoading}
            disabled={disabledField}
          >
            Salvar dados
          </Button>
        </Group>
      </Form>
    </Card>
  );
}
